import { Card, CardContent, CardHeader, CardTitle } from '@city-sage/aurum-avis-labs-components';

export default function TermsPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl font-bold">Terms & Conditions</CardTitle>
          <p className="text-muted-foreground">Last Updated: 01.11.2024</p>
        </CardHeader>
        <CardContent className="prose prose-gray max-w-none">
          <p>
            These Terms & Conditions ("Terms") govern your access to and use of CitySage, an application operated by Aurum Avis Labs GmbH ("CitySage," "we," or "us"). By accessing or using the CitySage application ("App") and its services, you agree to comply with these Terms. Please read them carefully.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">1. <strong>Acceptance of Terms</strong></h3>
          <p>
            By using CitySage, you agree to these Terms, as well as to any Community Guidelines or policies referenced within. If you do not agree to these Terms, please refrain from using the App.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">2. <strong>User Obligations and Community Guidelines</strong></h3>
          <p>
            CitySage users are expected to adhere to our Community Guidelines, designed to maintain a respectful and informative environment. In alignment with the <strong>Swiss Federal Act on Copyright and Related Rights (CopA)</strong>, users agree to:
          </p>
          <div className="ml-4 space-y-2">
            <p>- Use only verified sources when creating or sharing content within the App.</p>
            <p>- Respect intellectual property rights by ensuring they have the necessary rights to share any content uploaded to the platform.</p>
            <p>- Abstain from distributing inappropriate, false, or defamatory content, in line with <strong>Art. 28 of the Swiss Civil Code (ZGB)</strong> regarding personal rights and protection against infringement.</p>
          </div>

          <h3 className="text-xl font-bold mt-8 mb-4">3. <strong>Content and Liability Disclaimer</strong></h3>
          <p>
            CitySage provides a dual content model that includes both AI-generated and user-generated tours:
          </p>
          <div className="ml-4 space-y-2">
            <p>- <strong>AI-Generated Content</strong>: Tours created by our AI are based on data gathered from publicly available sources and provide general information on points of interest.</p>
            <p>- <strong>User-Generated Content</strong>: Users, including travelers, local experts, and professional guides, may create and share their own tours on the platform.</p>
          </div>
          <p>
            <strong>Disclaimer</strong>: In accordance with <strong>Art. 97 of the Swiss Code of Obligations (CO)</strong>, while CitySage undertakes reasonable measures to ensure content quality, we disclaim all liability for damages, losses, or other issues arising from reliance on third-party content or inaccuracies in AI-generated tours.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">4. <strong>Payment Terms</strong></h3>
          <p>
            CitySage offers premium tours available for one-time purchase. By completing a purchase, you acknowledge and accept that:
          </p>
          <div className="ml-4 space-y-2">
            <p>- All sales are final, and no refunds will be issued for any tour purchases.</p>
            <p>- Purchases are non-transferable and valid only for individual use within the App.</p>
            <p>- This is in line with <strong>Art. 184 of the Swiss Code of Obligations (CO)</strong> regarding the binding nature of purchase agreements.</p>
          </div>

          <h3 className="text-xl font-bold mt-8 mb-4">5. <strong>Refund Policy for Third-Party Services</strong></h3>
          <p>
            CitySage may offer links or recommendations to third-party services, such as museum tickets or other paid activities. CitySage is not responsible for these third-party services. Any refunds or disputes must be handled directly with the third-party provider, and CitySage holds no liability as per <strong>Art. 101 CO</strong> regarding third-party obligations.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">6. <strong>Intellectual Property</strong></h3>
          <p>
            All content within CitySage, including AI-generated content, branding, and proprietary technologies, is owned or licensed by Aurum Avis Labs GmbH. Unauthorized use, reproduction, or distribution of this content is prohibited under the <strong>Swiss Federal Act on Copyright and Related Rights (CopA)</strong>.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">7. <strong>Limitation of Liability</strong></h3>
          <p>
            In line with <strong>Art. 100 CO</strong>, CitySage disclaims liability for damages to the fullest extent permitted by Swiss law, including for indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, or data, resulting from:
          </p>
          <div className="ml-4 space-y-2">
            <p>- Your use of or inability to use the App.</p>
            <p>- Any conduct or content of any third party on the App, including defamatory, offensive, or illegal content posted by other users.</p>
            <p>- Any content obtained from the App.</p>
          </div>

          <h3 className="text-xl font-bold mt-8 mb-4">8. <strong>Governing Law and Jurisdiction</strong></h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of Switzerland. Any disputes arising from or related to these Terms, or your use of CitySage, shall be subject to the exclusive jurisdiction of the courts of Zurich, Switzerland.
          </p>
          <p>
            <strong>Compliance with EU Regulations</strong>: While governed by Swiss law, CitySage also complies with relevant <strong>EU regulations</strong> in relation to data protection and privacy, including the <strong>General Data Protection Regulation (GDPR)</strong>, specifically <strong>Articles 13–22 GDPR</strong>, which outline the rights of EU-based users to access, correct, or delete their personal data.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">9. <strong>Changes to the Terms</strong></h3>
          <p>
            We may modify these Terms from time to time to reflect changes in our services or applicable laws. In compliance with <strong>Art. 8 CO</strong>, CitySage will notify users of any material changes. Continued use of the App following such updates constitutes acceptance of the new Terms.
          </p>

          <h3 className="text-xl font-bold mt-8 mb-4">10. <strong>Contact Information</strong></h3>
          <p>
            If you have any questions about these Terms or wish to report a violation, please contact us at:
          </p>
          <div className="mt-4">
            <p className="font-bold">Aurum Avis Labs GmbH</p>
            <p>info@aurum-avis-labs.ch</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 